/**
 * <park-admin-page-header> — the title block every bespoke admin page opens
 * with, lifted 1:1 from the `.adm-pagehead` markup in `layouts/AdminShell.astro`
 * (anton/forrest#1000). Back-link, H1, muted intro, and a slot for page-specific
 * chips/links.
 *
 * It is the operator-facing counterpart of <park-page-header> (the public
 * site's), and it realises the "give every bespoke admin page a clear title
 * header" half of admin-shell#FR-6 as a component rather than layout markup.
 * Because AdminShell composes it, EVERY admin app page (/yllapito,
 * /galleria-hallinta, …) gets it — which is what makes it a recurring pattern
 * under design-system#FR-3 rather than one page's chrome.
 *
 * Single source of truth = the pure `renderParkAdminPageHeader()` function
 * below, consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper
 *     `components/ds/ParkAdminPageHeader.astro` (server-side, inside
 *     <park-admin-page-header data-ssr> so it is in the initial HTML), and
 *   - the Claude-Design preview, where the element renders client-side.
 *
 * The back link is omitted when `back-href` is empty — that is how the landing
 * page itself (which has nowhere to go back to) drops it.
 *
 * Attributes:
 *   - heading="…"     the page H1.
 *   - intro="…"       optional muted intro paragraph (omitted when empty).
 *   - back-href="…"   optional back-link target (omitted when empty).
 *   - back-label="…"  back-link text.
 * Slotted content = optional headmeta (chips, secondary links).
 *
 * Styling lives in wc/css/park-admin-page-header.css — no <style> here.
 *
 * @dsSpec design-system#FR-3 — the recurring operator page-title block.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-page-header";

export interface ParkAdminPageHeaderProps {
	heading?: string;
	intro?: string;
	backHref?: string;
	backLabel?: string;
}

/**
 * Pure render: (props, headmeta HTML) → inner HTML string. No DOM access, so it
 * is safe server-side and client-side. `headmetaHtml` is trusted author/server
 * markup and is emitted verbatim; back link and intro are omitted when empty,
 * matching the conditionals the AdminShell markup used.
 */
export function renderParkAdminPageHeader(
	props: ParkAdminPageHeaderProps,
	headmetaHtml = "",
): string {
	const back = props.backHref
		? html`<a class="pa-back" href="${esc(props.backHref)}">${esc(props.backLabel ?? "")}</a>`
		: "";
	const intro = props.intro ? html`<p>${esc(props.intro)}</p>` : "";
	return html`<header class="pa-pagehead">${back}<h1>${esc(props.heading ?? "")}</h1>${intro}${headmetaHtml}</header>`;
}

class ParkAdminPageHeader extends ParkElement {
	static get observedAttributes(): string[] {
		return ["heading", "intro", "back-href", "back-label"];
	}

	protected render(): string {
		return renderParkAdminPageHeader(
			{
				heading: this.attr("heading"),
				intro: this.attr("intro"),
				backHref: this.attr("back-href"),
				backLabel: this.attr("back-label"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkAdminPageHeader);

export { ParkAdminPageHeader };
